import { useEffect, useState, type ReactNode } from 'react'
import { PAYOUT_STATUS_LABEL, type PlayerStatement } from '@workflow/contracts'
import { Button, cn, Dialog, EmptyNote, IconButton, Icons, Loading, StateNote, StatusLabel, StatusMark } from '@workflow/ui'
import { describeError } from '@/lib/api/client'
import { useAcceptStatement, useDisputeStatement, useMyStatements, useOpenStatement } from '@/lib/api/hooks'
import { formatCreated } from '@/lib/format'
import { PageFrame } from '@/shell/PageFrame'
import { WritingSurface } from '@/features/writing/WritingSurface'
import { Markdown } from '@/features/announcements/Markdown'
import { PaymentSheetList, PaymentSheetListRow } from './PaymentSheetList'
import { SheetGrid, type Column, type Row } from './SheetGrid'
import { amountText, dayLabel, daysOf, hoursText, money, PAYOUT_HUE, periodLabel, rateWords } from './sheetMath'

/**
 * A player's Payments: one statement per month the studio shared — their own
 * row of the sheet, read-only, linked to it so a change there reads here.
 * What they did day by day, what each job paid, any extra and why, where the
 * money goes, and whether it has gone. They accept it, or dispute it with a
 * reason the studio answers.
 */
export function StatementsPage() {
  const statements = useMyStatements()
  const markOpened = useOpenStatement()
  const [chosenSheet, setChosenSheet] = useState('')
  const [sheetView, setSheetView] = useState(false)
  const list = statements.data ?? []
  const statement = list.find((entry) => entry.sheetId === chosenSheet)

  function openStatement(entry: PlayerStatement) {
    setChosenSheet(entry.sheetId)
    if (!entry.openedAt) markOpened.mutate(entry.sheetId)
  }

  return (
    <PageFrame title="Payments" description="Your monthly statements from Thunders Lab Studio.">
      {statements.isLoading ? (
        <Loading label="Loading your statements" />
      ) : statements.error ? (
        <StateNote title="Your statements could not be loaded." detail={describeError(statements.error)} />
      ) : list.length === 0 ? (
        <EmptyNote title="No statements yet." detail="When the studio shares a month's payment sheet, your part of it appears here." />
      ) : statement ? (
        <>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <Button size="sm" variant="ghost" icon={<Icons.back />} onClick={() => setChosenSheet('')}>All statements</Button>
            <Button size="sm" icon={sheetView ? <Icons.receipt /> : <Icons.spreadsheet />} onClick={() => setSheetView((current) => !current)}>
              {sheetView ? 'Receipt view' : 'Sheet view'}
            </Button>
          </div>
          <Statement key={statement.sheetId} statement={statement} sheetView={sheetView} />
        </>
      ) : (
        <>
          <h2 className="mb-3 text-micro text-muted-foreground uppercase">Payment sheets</h2>
          <PaymentSheetList>
            {list.map((entry) => (
              <PaymentSheetListRow
                key={entry.sheetId}
                title={entry.title}
                detail={periodLabel(entry.periodStart, entry.periodEnd)}
                indicator={!entry.openedAt ? (
                  <>
                    <span className="size-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
                    <span className="sr-only">Unread</span>
                  </>
                ) : null}
                onOpen={() => openStatement(entry)}
              >
                <StatusLabel tone="idle" hue={PAYOUT_HUE[entry.status] ?? undefined} label={PAYOUT_STATUS_LABEL[entry.status]} />
              </PaymentSheetListRow>
            ))}
          </PaymentSheetList>
        </>
      )}
    </PageFrame>
  )
}

function Statement({ statement, sheetView }: { statement: PlayerStatement; sheetView: boolean }) {
  const hours = statement.lines.reduce((sum, line) => sum + line.hours, 0)
  const amount = statement.lines.reduce((sum, line) => sum + (line.amount ?? 0), 0)
  const total = amount + statement.extraAmount
  const pending = statement.lines.some((line) => line.amount === null)
  const days = statement.lines
    .flatMap((line) => daysOf(line.entries, line.hours, statement.periodEnd).map((day) => ({ ...day, line })))
    .sort((a, b) => (a.day ?? '9999').localeCompare(b.day ?? '9999') || a.line.jobTitle.localeCompare(b.line.jobTitle))
  const hue = PAYOUT_HUE[statement.status]

  return (
    <div className="flex flex-col gap-5">
      <section className="rounded-2xl border border-hairline bg-surface p-5">
        <div className="flex flex-wrap items-start gap-4">
          <div className="min-w-0 flex-1">
            <p className="text-caption text-muted-foreground">{periodLabel(statement.periodStart, statement.periodEnd)}</p>
            <p className="mt-1 font-display text-[2rem] leading-none text-foreground tabular-nums">{amountText(total)}</p>
            <p className="mt-1.5 text-caption text-muted-foreground">
              {hoursText(hours)} hours across {statement.lines.length} {statement.lines.length === 1 ? 'job' : 'jobs'}
              {statement.extraAmount ? ` · includes ${money(statement.extraAmount)} extra` : ''}
            </p>
          </div>
          <span className={cn(
            'inline-flex h-7 items-center gap-1.5 rounded-full px-3 text-caption text-foreground',
            hue === 'green' ? 'bg-status-green/12' : hue === 'amber' ? 'bg-status-amber/12' : hue === 'red' ? 'bg-status-red/10' : 'bg-tint',
          )}>
            <StatusMark tone="idle" hue={hue ?? undefined} />
            {PAYOUT_STATUS_LABEL[statement.status]}
            {statement.statusAt && statement.status !== 'unpaid' ? <span className="text-muted-foreground">· {formatCreated(statement.statusAt)}</span> : null}
          </span>
        </div>
        {pending ? (
          <p className="mt-3 text-caption text-status-amber">Part of this month has no rate yet, so its amount is still being set by the studio.</p>
        ) : null}
        <Review statement={statement} />
      </section>

      <Ledger
        sheetView={sheetView}
        title="Work by day"
        head={['Date', 'Job', 'Client', 'Hours', 'Amount']}
        rows={days.map((day) => [
          day.day ? dayLabel(day.day) : <span className="text-muted-foreground">Undated</span>,
          day.line.jobTitle,
          <span className="text-muted-foreground">{day.line.clientName ?? 'Internal'}</span>,
          hoursText(day.hours),
          day.line.rateType === 'hourly' && day.line.rateAmount !== null ? money(Math.round(day.hours * day.line.rateAmount)) : '',
        ])}
        note="Undated hours were counted by the studio — on completion, or before the hours log kept dates."
        showNote={days.some((day) => day.day === null)}
      />

      <Ledger
        sheetView={sheetView}
        title="Payment breakdown by job"
        head={['Job', 'Client', 'Hours', 'Rate', 'Amount']}
        rows={[
          ...statement.lines.map((line) => [
            line.jobTitle,
            <span className="text-muted-foreground">{line.clientName ?? 'Internal'}</span>,
            hoursText(line.hours),
            rateWords(line.rateType, line.rateAmount) || <span className="text-status-amber">Being set</span>,
            money(line.amount),
          ]),
          ...(statement.extraAmount ? [[
            <span>Extra{statement.extraReason ? <span className="text-muted-foreground"> · {statement.extraReason}</span> : null}</span>, '', '', '', money(statement.extraAmount),
          ]] : []),
        ]}
        foot={['Total', '', hoursText(hours), '', amountText(total)]}
      />

      <section className="flex flex-wrap gap-x-10 gap-y-3 rounded-2xl border border-hairline bg-surface px-5 py-4 text-caption">
        <div>
          <p className="text-muted-foreground">Payment Information</p>
          <p className="mt-0.5 text-ui text-foreground">{statement.method ?? 'No method on your profile'}{statement.account ? <span className="ml-2 font-mono text-caption">{statement.account}</span> : null}</p>
        </div>
        <div>
          <p className="text-muted-foreground">Shared</p>
          <p className="mt-0.5 text-ui text-foreground">{formatCreated(statement.sharedAt)}</p>
        </div>
      </section>
    </div>
  )
}

/** Accept, or say what is wrong. Settled months (paid, cancelled) are past reviewing. */
function Review({ statement }: { statement: PlayerStatement }) {
  const accept = useAcceptStatement()
  const [disputing, setDisputing] = useState(false)
  const settled = statement.status === 'paid' || statement.status === 'cancelled'
  return (
    <div className="mt-4 border-t border-hairline pt-4">
      {statement.review === 'accepted' ? (
        <p className="flex items-center gap-2 text-caption text-foreground">
          <span className="text-status-green"><Icons.check size="sm" /></span>
          You accepted this statement{statement.reviewAt ? ` ${formatCreated(statement.reviewAt)}` : ''}.
        </p>
      ) : statement.review === 'disputed' ? (
        <div className="text-caption">
          <p className="flex items-center gap-2 text-danger">
            <Icons.error size="sm" />
            You disputed this{statement.reviewAt ? ` ${formatCreated(statement.reviewAt)}` : ''}. The studio will look at it and send it back.
          </p>
          {statement.dispute ? <Markdown source={statement.dispute} className="mt-2 border-l-2 border-hairline pl-3 text-ui text-foreground" /> : null}
        </div>
      ) : settled ? null : (
        <p className="text-caption text-muted-foreground">Check the days and amounts below. Does this look right?</p>
      )}
      {!settled && statement.review !== 'accepted' ? (
        <div className="mt-3 flex gap-2">
          <Button size="sm" variant="primary" icon={<Icons.check />} busy={accept.isPending} onClick={() => accept.mutate(statement.sheetId)}>
            Accept
          </Button>
          <Button size="sm" onClick={() => setDisputing(true)}>{statement.review === 'disputed' ? 'Edit dispute' : 'Dispute'}</Button>
        </div>
      ) : null}
      <DisputeWindow open={disputing} onClose={() => setDisputing(false)} statement={statement} />
    </div>
  )
}

/** The dispute is written on the same page-like surface as notes and announcements. */
function DisputeWindow({ open, onClose, statement }: { open: boolean; onClose: () => void; statement: PlayerStatement }) {
  const [reason, setReason] = useState('')
  const dispute = useDisputeStatement(onClose)
  useEffect(() => {
    if (!open) return
    setReason(statement.dispute ?? '')
    dispute.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])
  const ready = reason.trim().length >= 3
  const send = () => {
    if (ready && !dispute.isPending) dispute.mutate({ sheetId: statement.sheetId, reason: reason.trim() })
  }
  return (
    <Dialog open={open} onClose={onClose} labelledBy="dispute-label" className="sm:max-w-2xl">
      <form
        className="flex max-h-[calc(100dvh-4rem)] flex-col rounded-3xl border bg-surface shadow-modal"
        onSubmit={(event) => {
          event.preventDefault()
          send()
        }}
      >
        <div className="flex items-center justify-between px-6 pt-5 sm:px-8">
          <p id="dispute-label" className="text-micro text-muted-foreground uppercase">Dispute {statement.title}</p>
          <IconButton label="Close" size="sm" onClick={onClose} tooltipSide="bottom"><Icons.close /></IconButton>
        </div>
        <WritingSurface
          value={reason}
          onChange={setReason}
          onSubmit={send}
          label="What is wrong"
          placeholder="Which day or job is off, and what it should be…"
          maxLength={4000}
          minHeight="min-h-[8rem]"
          autoFocus
          panels={dispute.error ? (
            <p key="fault" className="border-t border-hairline px-6 py-2.5 text-caption text-danger sm:px-8">{describeError(dispute.error)}</p>
          ) : null}
          actions={
            <>
              <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
              <Button type="submit" variant="primary" size="sm" busy={dispute.isPending} disabled={!ready}>Send to the studio</Button>
            </>
          }
        />
      </form>
    </Dialog>
  )
}

/** A small read-only grid in the sheet's own lines. */
function Ledger({ title, head, rows, foot, note, showNote = false, sheetView }: {
  sheetView: boolean
  title: string
  head: string[]
  rows: ReactNode[][]
  foot?: ReactNode[]
  note?: string
  showNote?: boolean
}) {
  const right = (index: number) => index >= head.length - 2
  return (
    <section>
      <h2 className="mb-2 text-ui font-medium text-foreground">{title}</h2>
      {sheetView ? (
        <StatementGrid title={title} head={head} rows={rows} foot={foot} />
      ) : (
        <>
          <LedgerList rows={rows} foot={foot} />
          <div className="hidden overflow-x-auto rounded-xl border border-hairline bg-surface sm:block">
            <table className="w-full border-separate border-spacing-0 text-caption tabular-nums">
              <thead>
                <tr>
                  {head.map((label, index) => (
                    <th key={label} className={cn('h-8 border-b border-hairline bg-surface-sunken px-3 font-normal text-muted-foreground', index > 0 && 'border-l', right(index) ? 'text-right' : 'text-left')}>
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {row.map((value, index) => (
                      <td key={index} className={cn('h-8 border-b border-hairline px-3 text-foreground whitespace-nowrap', index > 0 && 'border-l', right(index) ? 'text-right' : 'text-left')}>
                        {value}
                      </td>
                    ))}
                  </tr>
                ))}
                {rows.length === 0 ? (
                  <tr><td colSpan={head.length} className="h-10 px-3 text-center text-muted-foreground">Nothing here.</td></tr>
                ) : null}
              </tbody>
              {foot ? (
                <tfoot>
                  <tr>
                    {foot.map((value, index) => (
                      <td key={index} className={cn('h-8 bg-surface-sunken/60 px-3 font-medium text-foreground', index > 0 && 'border-l border-hairline', right(index) ? 'text-right' : 'text-left')}>
                        {value}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </div>
        </>
      )}
      {note && showNote ? <p className="mt-1.5 text-caption text-muted-foreground">{note}</p> : null}
    </section>
  )
}

/** Use the admin grid renderer with only the cells in this player's statement. */
function StatementGrid({ title, head, rows, foot }: {
  title: string
  head: string[]
  rows: ReactNode[][]
  foot?: ReactNode[]
}) {
  const columns: Column[] = head.map((label, index) => ({
    key: label,
    label,
    width: label === 'Job' ? 168 : label === 'Client' ? 130 : 90,
    align: index >= head.length - 2 ? 'right' : 'left',
  }))
  const row = (values: ReactNode[], index: number, kind: Row['kind']): Row => ({
    key: String(index), number: index + 1, kind,
    cells: Object.fromEntries(columns.map(({ key }, i) => {
      const value = values[i]
      const detail = typeof value === 'string' || typeof value === 'number' ? String(value) : key
      return [key, { view: value, detail }]
    })),
  })
  return (
    <SheetGrid label={title} grid={{
      columns,
      rows: [
        row(head, 0, 'head'),
        ...rows.map((values, i) => row(values, i + 1, 'body')),
        ...(foot ? [row(foot, rows.length + 1, 'total')] : []),
      ],
    }} emptyMessage={rows.length ? undefined : 'Nothing here.'} />
  )
}

/**
 * The same lines on a phone: each row in two — what it is and its amount,
 * then the rest beneath — so nothing scrolls sideways.
 */
function LedgerList({ rows, foot }: { rows: ReactNode[][]; foot?: ReactNode[] }) {
  const line = (row: ReactNode[], key: string | number, strong = false) => {
    const rest = row.slice(1, -1).filter((value) => value !== '' && value !== null && value !== undefined)
    return (
      <li key={key} className={cn('px-4 py-3', strong && 'bg-surface-sunken/60')}>
        <div className={cn('flex items-baseline justify-between gap-3 text-ui text-foreground', strong && 'font-medium')}>
          <span className="min-w-0 truncate">{row[0]}</span>
          <span className="shrink-0 tabular-nums">{row[row.length - 1]}</span>
        </div>
        {rest.length > 0 ? (
          <p className="mt-0.5 truncate text-caption text-muted-foreground tabular-nums">
            {rest.map((value, index) => <span key={index}>{index > 0 ? ' · ' : ''}{value}</span>)}
          </p>
        ) : null}
      </li>
    )
  }
  return (
    <ul className="divide-y divide-hairline overflow-hidden rounded-xl border border-hairline bg-surface sm:hidden">
      {rows.map((row, index) => line(row, index))}
      {rows.length === 0 ? <li className="px-4 py-3 text-center text-caption text-muted-foreground">Nothing here.</li> : null}
      {foot ? line(foot, 'foot', true) : null}
    </ul>
  )
}
